"use client";

import { useMemo } from "react";

import type { AnalysisKey, QualityFlag } from "@/lib/music/analyzer-types";
import type { TitleBadgeState } from "@/lib/api/title-suggestion";
import type { WorkRights } from "@/lib/music/analyzer-types";
import { workRightsNotice } from "@/lib/upload/work-rights-notice";
import type { UploadFileKind } from "@/lib/upload/score-file";

import { Eyebrow, HintPill } from "@/components/ui";
import { TempoControl } from "../../molecules/tempo-control";

import { PieceCard } from "./piece-card";
import { WarningBlock } from "./warning-block";

/**
 * Phase rendered by the meta step. Drives which visual is shown:
 * - `analyzing`: skeleton card + cycling hint pill.
 * - `ready`   : editable card + controls.
 */
export type StepMetaPhase = "analyzing" | "ready";

const ANALYSIS_HINTS_MIDI: readonly string[] = [
  "Virtuosa lit ton fichier",
  "Cherche la tonalité",
  "Repère les pistes piano",
  "Compte les mesures",
];

const ANALYSIS_HINTS_MUSICXML: readonly string[] = [
  "Virtuosa lit ta partition",
  "Cherche la tonalité",
  "Lit les mains sur les portées",
  "Compte les mesures",
];

const ANALYSIS_HINTS_IMAGE: readonly string[] = [
  "Lecture de la partition",
  "Identifie les notes",
  "Cherche la tonalité",
  "Repère le tempo",
];

const ANALYSIS_HINTS: Record<UploadFileKind, readonly string[]> = {
  midi: ANALYSIS_HINTS_MIDI,
  musicxml: ANALYSIS_HINTS_MUSICXML,
  image: ANALYSIS_HINTS_IMAGE,
};

/**
 * Default quality label shown by the OK / OK-with-hints flows. The
 * analyzer doesn't return a free-form copy yet, so the front owns the
 * verbiage.
 */
const QUALITY_LABEL_OK = "Analyse fiable";
const QUALITY_LABEL_OK_WITH_HINTS = "Analyse correcte";

interface StepMetaProps {
  /** Drives skeleton vs editable rendering. */
  phase: StepMetaPhase;
  /** Family of the imported file, driving the glyph and the analysis copy. */
  source: UploadFileKind;
  /** Pre-formatted filename (e.g. `zombie.mid`). */
  filename: string;
  /** Pre-formatted size (e.g. `"14 ko"`). */
  fileSize: string;

  title: string;
  artist: string;
  tempo: number;
  detectedKey?: AnalysisKey | "loading" | null;
  /**
   * Active hints from `quality.kind === "ok_with_hints"`, already scoped to
   * the file's format by the host. When non-empty, a `WarningBlock` is
   * rendered above the controls.
   */
  qualityHints?: readonly QualityFlag[];
  /**
   * Wording for those hints, resolved by the host so this component stays a
   * view. Falls back to a generic line when no flag has a known wording.
   */
  qualityMessage?: string | null;
  /** Quality score 1..5 for the hero card. */
  quality?: number;
  /** Optional override for the quality label. */
  qualityLabel?: string;

  onTitle: (v: string) => void;
  onArtist: (v: string) => void;
  onTempo: (v: number) => void;

  /** True for OCR / image mode (shows tempo picker when undetected). */
  showOcrOptions?: boolean;
  /** When true (OCR), no tempo was detected from the score. */
  ocrTempoMissing?: boolean;
  /** Suggestion-badge state for the title (currently unused in V2). */
  titleBadge?: TitleBadgeState;
  /** Suggestion-badge state for the artist (currently unused in V2). */
  artistBadge?: TitleBadgeState;
  /** Broader work the suggestion belongs to (e.g. "BO de Pirates …"). */
  suggestedContext?: string | null;
  /**
   * Le verdict de droits de l'œuvre, rendu sous le titre.
   *
   * Ici et pas après le dépôt : c'est le seul moment où le compositeur est
   * encore modifiable, et la détection se fait sur son nom.
   */
  workRights?: WorkRights | null;
}

function detectedKeyLabel(
  detectedKey: AnalysisKey | "loading" | null | undefined,
  showOcrOptions: boolean | undefined,
): string | undefined {
  if (showOcrOptions) {
    return undefined;
  }

  if (detectedKey === "loading" || detectedKey == null) {
    return undefined;
  }

  return detectedKey.label;
}

/**
 * Renders the "Details" step of the upload modal in V2 form. Combines:
 * - the `PieceCard` hero (analyzing skeleton or editable meta + verdict),
 * - the cycling `HintPill` during analysis,
 * - the optional `WarningBlock` for the `ok_with_hints` flow,
 * - the OCR-only tempo card.
 *
 * Stateless. Title / composer state lives in the parent (`UploadModal`) so
 * the anti-stomp ref can guard against late auto-fill.
 */
export function StepMeta({
  phase,
  source,
  filename,
  fileSize,
  title,
  artist,
  tempo,
  detectedKey,
  qualityHints,
  qualityMessage,
  quality,
  qualityLabel,
  onTitle,
  onArtist,
  onTempo,
  showOcrOptions,
  ocrTempoMissing = false,
  suggestedContext,
  workRights,
}: StepMetaProps) {
  const hints = ANALYSIS_HINTS[source];

  const resolvedQualityLabel = useMemo(() => {
    if (qualityLabel) {
      return qualityLabel;
    }

    if (qualityHints && qualityHints.length > 0) {
      return QUALITY_LABEL_OK_WITH_HINTS;
    }

    return QUALITY_LABEL_OK;
  }, [qualityLabel, qualityHints]);

  const keyLabel = detectedKeyLabel(detectedKey, showOcrOptions);
  const isAnalyzing = phase === "analyzing";

  return (
    <div className="flex flex-col gap-5 px-6 py-5">
      <PieceCard
        filename={filename}
        fileSize={fileSize}
        source={source}
        title={title}
        composer={artist}
        onTitleChange={onTitle}
        onComposerChange={onArtist}
        detectedKey={keyLabel}
        quality={quality}
        qualityLabel={resolvedQualityLabel}
        loading={isAnalyzing}
        footerExtras={
          isAnalyzing ? null : (
            <>
              {suggestedContext ? (
                <p
                  className="text-[11px] italic"
                  style={{ color: "var(--color-text-muted)" }}
                  title="Contexte suggéré par l'IA, disparaît dès que tu modifies le titre"
                >
                  {suggestedContext}
                </p>
              ) : null}
              <RightsNotice rights={workRights} />
              <UploadDeclaration />
            </>
          )
        }
      />

      {isAnalyzing && (
        <div className="flex justify-center">
          <HintPill hints={hints} />
        </div>
      )}

      {!isAnalyzing && qualityHints && qualityHints.length > 0 && (
        <WarningBlock
          title="Quelques points à vérifier"
          body={
            qualityMessage ??
            "On va analyser ton fichier comme il est, mais le rendu peut être imprécis."
          }
        />
      )}

      {!isAnalyzing && showOcrOptions && (
        <div
          className="p-4"
          style={{
            borderRadius: "var(--radius-card)",
            backgroundColor: "rgba(255, 255, 255, 0.02)",
            border: "1px solid var(--color-border-subtle)",
          }}
        >
          <div className="flex items-baseline justify-between mb-2">
            <Eyebrow>Tempo</Eyebrow>
            {ocrTempoMissing && (
              <span
                className="text-[10px]"
                style={{ color: "var(--color-text-muted)" }}
              >
                Non détecté dans la partition
              </span>
            )}
          </div>
          <TempoControl value={tempo} onChange={onTempo} />
        </div>
      )}
    </div>
  );
}

/**
 * La mention de droits, sous le titre.
 *
 * # Sans couleur d'alerte
 *
 * Ni bordure rouge, ni pictogramme d'avertissement. Personne n'a rien fait de
 * mal en déposant une chanson qu'il travaille, et le produit ne le lui reproche
 * pas. C'est une information sur ce qui va se passer, rendue dans le gris des
 * autres informations d'appoint.
 *
 * # Deux niveaux, et le second est plus petit
 *
 * La conséquence d'abord, parce que c'est ce dont la personne a besoin : où va
 * ce morceau. Le motif ensuite, en plus discret, parce qu'il ne sert qu'à qui
 * veut vérifier — et c'est lui qui permet de repérer qu'on s'est trompé de
 * compositeur, pendant que le champ est encore modifiable.
 */
function RightsNotice({ rights }: { rights: WorkRights | null | undefined }) {
  const notice = workRightsNotice(rights);

  if (notice === null) {
    return null;
  }

  return (
    <p className="text-[11px] leading-[15px]">
      <span style={{ color: "var(--color-text-secondary)" }}>
        {notice.consequence}
      </span>{" "}
      <span style={{ color: "var(--color-text-muted)" }}>{notice.reason}</span>
    </p>
  );
}

/**
 * La déclaration de droits, sous le bouton qui valide l'import.
 *
 * # Pourquoi ici et pas à l'inscription
 *
 * Au moment de créer un compte, personne n'a encore déposé quoi que ce soit :
 * une clause sur des fichiers qui n'existent pas est du bruit que l'œil saute.
 * Ici, elle porte sur le geste qu'on est en train de faire, et c'est aussi là
 * qu'un juge la trouverait pertinente.
 *
 * # Une phrase, pas un mur
 *
 * La garantie et ses conséquences vivent dans les conditions générales, où
 * personne ne les lit et c'est normal : elles servent en cas de litige. Ce qui
 * doit être lu ici tient en deux propositions, ce que tu déclares et où va le
 * fichier.
 *
 * # Pas de case à cocher
 *
 * Même raison qu'à l'inscription : une case ajoute un clic sans rien prouver de
 * plus. Ce qui prouve, c'est la version des conditions enregistrée sur le
 * compte, avec sa date.
 */
function UploadDeclaration() {
  return (
    <p className="text-[11px] leading-[15px]" style={{ color: "var(--color-text-muted)" }}>
      En important, tu déclares avoir le droit d&apos;utiliser ce fichier. Il
      reste dans ta bibliothèque.{" "}
      <a
        href="/conditions"
        target="_blank"
        rel="noreferrer noopener"
        className="underline underline-offset-2 hover:text-text-secondary"
      >
        Conditions générales
      </a>
    </p>
  );
}
